/* ──────────────────────────────────────────────────────────────────────
 * FollowUpDetailModal — lightweight operational modal opened from the
 * dashboard (Latest Lead Updates, Today's Follow-ups, My Desk tasks,
 * inline activity rows).
 *
 * Goal: let staff review a follow-up, see customer context, take an
 * action (call/sms/email/note), or complete/snooze the follow-up —
 * WITHOUT leaving the dashboard. "Open Full Lead" is still one click
 * away for the deep-dive flow.
 *
 * Data: lazy-fetched via fetchLeadThread(id) on open. AI suggestions
 * lazy-fetched via fetchAiSuggestions(id) on user request (not on open
 * — keeps the initial render snappy).
 * ──────────────────────────────────────────────────────────────────── */

import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, Box, Typography, Stack, Chip, Button,
  IconButton, CircularProgress, Divider, useMediaQuery, useTheme,
  Tooltip, Collapse, Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CallIcon from "@mui/icons-material/Call";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import HandshakeIcon from "@mui/icons-material/Handshake";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate } from "react-router-dom";
import UserAvatar from "../../Components/UserAvatar";
import QuickActionsCard from "../LeadSystem/QuickActionsCard";
import {
  fetchLeadThread,
  fetchAiSuggestions,
  fetchTeamUsers,
  type LeadThread,
  type AiSuggestion,
  type TeamUser,
} from "../LeadSystem/leadApi";

const INK = "#0F172A";
const CHAMPAGNE = "#C9A961";
const CHAMPAGNE_DEEP = "#A8893E";
const CHAMPAGNE_PALE = "#F5EFE0";
const HAIRLINE = "rgba(10,14,26,0.06)";
const FONT_MONO = `'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace`;

/* Status palette — mirrors LeadInquiries / DCCDashboard tokens so the
 * modal feels native to the rest of the app. */
const STATUS_COLOR: Record<string, string> = {
  new: "#3b82f6",
  contacted: "#0891b2",
  ready_for_quote: "#f59e0b",
  ready_for_booking: "#8b5cf6",
  confirmed: "#16a34a",
  lost: "#94a3b8",
};
const STATUS_LABEL: Record<string, string> = {
  new: "New Inquiry",
  contacted: "Contacted",
  ready_for_quote: "Ready for Quote",
  ready_for_booking: "Ready for Booking",
  confirmed: "Confirmed",
  lost: "Lost",
};
const PRIORITY_COLOR: Record<string, string> = {
  low: "#94a3b8",
  medium: "#3b82f6",
  high: "#f59e0b",
  urgent: "#dc2626",
};

interface Props {
  /** Lead thread id to display. Null/undefined keeps the modal closed. */
  leadId: number | null;
  onClose: () => void;
  /** Fired after a successful action inside the modal so the dashboard
   *  can refresh its feeds. Receives the lead id that changed. */
  onChanged?: (leadId: number) => void;
}

/* ── Date helpers ───────────────────────────────────────────────────── */
const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const fmtDateTime = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isFinite(d.getTime())
    ? d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "—";
};

const fmtRelative = (iso?: string | null): string => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!isFinite(ms)) return "";
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
};

/* Categorize follow-up timing into one of the 4 operational states the
 * spec calls out (overdue / today / upcoming / completed). Used both
 * for the header rail and the timing chip. */
function followUpTiming(dueAt: string | null | undefined, completedAt: string | null | undefined):
  { key: "completed" | "overdue" | "today" | "upcoming" | "none"; color: string; label: string } {
  if (completedAt) return { key: "completed", color: "#16a34a", label: "Completed" };
  if (!dueAt) return { key: "none", color: "#94a3b8", label: "No follow-up" };
  const due = new Date(dueAt);
  if (!isFinite(due.getTime())) return { key: "none", color: "#94a3b8", label: "No follow-up" };
  const now = new Date();
  if (due.getTime() < now.getTime()) return { key: "overdue", color: "#dc2626", label: "Overdue" };
  const sameDay = due.toDateString() === now.toDateString();
  if (sameDay) return { key: "today", color: "#f97316", label: "Due Today" };
  return { key: "upcoming", color: "#3b82f6", label: "Upcoming" };
}

/* ── Activity descriptor ────────────────────────────────────────────────
 * Classify a raw activity row into the operator-facing fields the
 * Recent Activity / Last Interaction renderers need:
 *   - icon + color (visual scan)
 *   - title (top line, e.g. "Call logged")
 *   - outcome chip text + tone (contact_logged only)
 *   - body lines (notes, SMS body, email subject, status diff)
 *   - metadata rows (due_at, phone, recipient, etc.)
 *
 * Pulled from `meta` (cast to array on the backend, shipped verbatim by
 * LeadController). See backend meta-key audit for what's actually
 * populated per type. */
type Tone = "success" | "warning" | "info" | "violet" | "danger" | "neutral";

const TONE: Record<Tone, { bg: string; fg: string; border: string }> = {
  success: { bg: "#dcfce7", fg: "#15803d", border: "#bbf7d0" },
  warning: { bg: "#fef3c7", fg: "#b45309", border: "#fde68a" },
  info:    { bg: "#dbeafe", fg: "#1d4ed8", border: "#bfdbfe" },
  violet:  { bg: "#ede9fe", fg: "#6d28d9", border: "#ddd6fe" },
  danger:  { bg: "#fee2e2", fg: "#b91c1c", border: "#fecaca" },
  neutral: { bg: "#f1f5f9", fg: "#475569", border: "#e2e8f0" },
};

interface ActivityDescriptor {
  icon: React.ReactNode;
  iconColor: string;
  title: string;
  outcome?: { label: string; tone: Tone };
  /** Free-form quoted body (note text, SMS preview, email body). */
  body?: string;
  /** Structured metadata rows shown as label/value pairs. */
  rows?: { label: string; value: string; mono?: boolean; valueColor?: string }[];
}

function describeActivity(a: any): ActivityDescriptor {
  const meta = a.meta || {};

  switch (a.type) {
    case "contact_logged": {
      const sub = String(meta.type || "contact").toLowerCase();
      const icon = sub === "call" ? <CallIcon sx={{ fontSize: 14 }} />
        : sub === "email" ? <MailOutlineIcon sx={{ fontSize: 14 }} />
        : sub === "meeting" || sub === "in_person" ? <HandshakeIcon sx={{ fontSize: 14 }} />
        : sub === "voicemail" ? <CallIcon sx={{ fontSize: 14 }} />
        : <EditNoteIcon sx={{ fontSize: 14 }} />;
      const titleVerb = sub === "voicemail" ? "Voicemail left"
        : sub === "meeting" || sub === "in_person" ? "Meeting logged"
        : sub === "note" ? "Note logged"
        : `${sub.charAt(0).toUpperCase()}${sub.slice(1)} logged`;
      const outcomeRaw = meta.outcome || meta.follow_up_outcome;
      // Map outcome enum → tone. Negative-ish outcomes get a warm tone
      // so the operator's eye picks them out of a long feed.
      const outcomeTone: Tone =
        outcomeRaw === "no_answer" || outcomeRaw === "voicemail" || outcomeRaw === "callback_requested" ? "warning"
        : outcomeRaw === "not_interested" ? "danger"
        : "success";
      return {
        icon,
        iconColor: "#16a34a",
        title: titleVerb,
        outcome: outcomeRaw ? {
          label: String(outcomeRaw).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          tone: outcomeTone,
        } : undefined,
        body: typeof meta.notes === "string" ? meta.notes.trim() || undefined : undefined,
      };
    }

    case "note_added":
      return {
        icon: <EditNoteIcon sx={{ fontSize: 14 }} />,
        iconColor: "#f59e0b",
        title: "Internal note added",
        // For note_added the note text lives in `description`, not meta.
        body: typeof a.description === "string" ? a.description.trim() || undefined : undefined,
      };

    case "email_sent": {
      const subject = meta.subject ? String(meta.subject) : null;
      const to = meta.to ? String(meta.to) : null;
      return {
        icon: <MailOutlineIcon sx={{ fontSize: 14 }} />,
        iconColor: "#3b82f6",
        title: "Email sent",
        body: subject ? `Subject: ${subject}` : undefined,
        rows: to ? [{ label: "To", value: to }] : undefined,
      };
    }

    case "email_received": {
      const subject = meta.subject ? String(meta.subject) : null;
      return {
        icon: <MailOutlineIcon sx={{ fontSize: 14 }} />,
        iconColor: "#0891b2",
        title: "Email received",
        body: subject ? `Subject: ${subject}` : a.description || undefined,
      };
    }

    case "sms_sent":
    case "sms_failed": {
      const failed = a.type === "sms_failed";
      const preview = typeof meta.preview === "string" ? meta.preview : null;
      const rows: ActivityDescriptor["rows"] = [];
      if (meta.phone) rows.push({ label: "Sent to", value: String(meta.phone), mono: true });
      if (failed && meta.error_message) rows.push({
        label: "Error", value: String(meta.error_message), valueColor: "#b91c1c",
      });
      return {
        icon: <SmsOutlinedIcon sx={{ fontSize: 14 }} />,
        iconColor: failed ? "#dc2626" : "#0891b2",
        title: failed ? "SMS failed" : "SMS sent",
        body: preview || undefined,
        rows: rows.length ? rows : undefined,
      };
    }

    case "status_changed": {
      const from = a.changes?.status?.from?.label
        || a.changes?.status?.from?.value
        || meta.status_from || "";
      const to = a.changes?.status?.to?.label
        || a.changes?.status?.to?.value
        || meta.status_to || "";
      const pretty = (s: string) => String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return {
        icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
        iconColor: "#8b5cf6",
        title: from && to ? `Status: ${pretty(from)} → ${pretty(to)}`
          : to ? `Status changed → ${pretty(to)}`
          : "Status changed",
      };
    }

    case "assigned":
    case "lead_assigned": {
      const from = a.changes?.assigned_to?.from?.label;
      const to = a.changes?.assigned_to?.to?.label;
      return {
        icon: <HistoryIcon sx={{ fontSize: 14 }} />,
        iconColor: "#6b5a33",
        title: from && to ? `Reassigned: ${from} → ${to}`
          : to ? `Assigned to ${to}`
          : (a.description || "Lead assigned"),
      };
    }

    case "task_created": {
      // Title may be in meta.title, or embedded in description as
      // "Task created: <title>" / "Follow-up scheduled: <title>".
      const fromDesc = typeof a.description === "string"
        ? a.description.replace(/^(Task created|Follow-up scheduled):\s*/i, "").trim()
        : "";
      const taskTitle = (typeof meta.title === "string" && meta.title.trim())
        || fromDesc
        || null;
      const rows: ActivityDescriptor["rows"] = [];
      if (taskTitle) rows.push({ label: "Task", value: taskTitle });
      if (meta.due_at) rows.push({
        label: "Due", value: fmtDateTime(meta.due_at), valueColor: "#b45309",
      });
      return {
        icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
        iconColor: "#f97316",
        title: "Follow-up scheduled",
        body: typeof meta.notes === "string" ? meta.notes.trim() || undefined : undefined,
        rows: rows.length ? rows : undefined,
      };
    }

    case "task_completed": {
      const fromDesc = typeof a.description === "string"
        ? a.description.replace(/^Follow-up completed:\s*/i, "").trim()
        : "";
      const taskTitle = (typeof meta.title === "string" && meta.title.trim()) || fromDesc;
      const rows: ActivityDescriptor["rows"] = [];
      if (taskTitle) rows.push({ label: "Task", value: taskTitle });
      return {
        icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
        iconColor: "#16a34a",
        title: "Follow-up completed",
        rows: rows.length ? rows : undefined,
      };
    }

    case "task_rescheduled": {
      const fromTs = meta.due_at_from ? fmtDateTime(meta.due_at_from) : null;
      const toTs = meta.due_at_to ? fmtDateTime(meta.due_at_to) : null;
      return {
        icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
        iconColor: "#f97316",
        title: "Follow-up rescheduled",
        rows: fromTs && toTs ? [{ label: "Moved", value: `${fromTs} → ${toTs}` }] : undefined,
      };
    }

    default:
      return {
        icon: <HistoryIcon sx={{ fontSize: 14 }} />,
        iconColor: "#64748b",
        title: a.description || String(a.type || "Activity").replace(/_/g, " "),
      };
  }
}

/* ── ActivityRow — operator-facing row used in both "Last Interaction"
 * and "Recent Activity". Renders icon · title · outcome chip on the
 * first line; quoted body on the second; structured label/value rows
 * (due_at, phone, recipient) beneath; actor avatar + timestamp on the
 * right. Long bodies clamp to 2 lines with a tap-to-expand. */
function ActivityRow({
  activity, density = "comfortable",
}: { activity: any; density?: "comfortable" | "compact" }) {
  const d = describeActivity(activity);
  const compact = density === "compact";
  const [expanded, setExpanded] = useState(false);
  const body = d.body || "";
  const longBody = body.length > 120 || body.includes("\n");
  const actor = activity.actor_name
    || (activity.actor_type === "system" ? "System" : null);

  return (
    <Box sx={{
      display: "flex", gap: 1.1, alignItems: "flex-start",
      py: compact ? 0.7 : 1, px: compact ? 0.6 : 1,
      borderRadius: "8px",
      "&:hover": { bgcolor: "#fafbfc" },
    }}>
      {/* Icon column */}
      <Box sx={{
        flexShrink: 0,
        width: 26, height: 26, mt: 0.1,
        borderRadius: "50%",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        bgcolor: `${d.iconColor}14`, color: d.iconColor,
      }}>
        {d.icon}
      </Box>

      {/* Content column */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={0.7} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK, lineHeight: 1.3 }}>
            {d.title}
          </Typography>
          {d.outcome && (
            <Box sx={{
              display: "inline-flex", alignItems: "center",
              px: 0.6, py: 0.05, borderRadius: "4px",
              fontSize: 9.5, fontWeight: 800, letterSpacing: 0.3,
              textTransform: "uppercase",
              bgcolor: TONE[d.outcome.tone].bg,
              color: TONE[d.outcome.tone].fg,
              border: `1px solid ${TONE[d.outcome.tone].border}`,
            }}>
              {d.outcome.label}
            </Box>
          )}
        </Stack>

        {body && (
          <Box
            onClick={longBody ? () => setExpanded((v) => !v) : undefined}
            sx={{
              mt: 0.5,
              borderLeft: `2px solid ${d.iconColor}33`,
              pl: 1, py: 0.1,
              cursor: longBody ? "pointer" : "default",
            }}
          >
            <Typography sx={{
              fontSize: 11.8, color: "#475569", lineHeight: 1.5,
              fontStyle: "italic", whiteSpace: "pre-wrap", wordBreak: "break-word",
              ...(longBody && !expanded ? {
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              } : {}),
            }}>
              “{body}”
            </Typography>
            {longBody && (
              <Typography sx={{
                mt: 0.2, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4,
                textTransform: "uppercase", color: "#94a3b8",
              }}>
                {expanded ? "Show less" : "View more"}
              </Typography>
            )}
          </Box>
        )}

        {d.rows && d.rows.length > 0 && (
          <Stack gap={0.15} sx={{ mt: 0.5 }}>
            {d.rows.map((r, i) => (
              <Stack key={i} direction="row" gap={0.8} alignItems="baseline">
                <Typography sx={{
                  fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5,
                  color: "#94a3b8", textTransform: "uppercase",
                  minWidth: 56,
                }}>
                  {r.label}
                </Typography>
                <Typography sx={{
                  fontSize: 11.5, fontWeight: 600, color: r.valueColor || "#334155",
                  fontFamily: r.mono ? FONT_MONO : undefined,
                  lineHeight: 1.35, wordBreak: "break-word",
                }}>
                  {r.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}

        {actor && (
          <Typography sx={{
            mt: 0.5, fontSize: 10, color: "#94a3b8", fontWeight: 600,
          }}>
            by {actor}
          </Typography>
        )}
      </Box>

      {/* Timestamp */}
      <Typography sx={{
        fontSize: 10.5, color: "#94a3b8",
        fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
        mt: 0.3, flexShrink: 0,
      }}>
        {fmtRelative(activity.created_at)}
      </Typography>
    </Box>
  );
}

export default function FollowUpDetailModal({ leadId, onClose, onChanged }: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const open = leadId !== null;

  const [lead, setLead] = useState<LeadThread | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [showAi, setShowAi] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<AiSuggestion[] | null>(null);

  /* Lazy-fetch lead + team list when the modal opens. Team list is
   * needed by the embedded QuickActionsCard for the assignee selector
   * on "Schedule Follow-up". One round-trip each, in parallel. */
  useEffect(() => {
    if (leadId === null) return;
    setLead(null);
    setError(null);
    setShowAi(false);
    setAiSuggestions(null);
    setAiError(null);
    setLoading(true);
    Promise.all([
      fetchLeadThread(leadId),
      // Team list is cheap + cached on the server; safe to refetch.
      fetchTeamUsers().catch(() => [] as TeamUser[]),
    ])
      .then(([l, team]) => {
        setLead(l);
        setTeamUsers(Array.isArray(team) ? team : []);
      })
      .catch((e: any) => setError(e?.message || "Could not load lead"))
      .finally(() => setLoading(false));
  }, [leadId]);

  /* Re-fetch the lead after an action so timeline + follow-up state
   * update inside the modal without a full close/reopen cycle. */
  const refresh = async () => {
    if (leadId === null) return;
    try {
      const l = await fetchLeadThread(leadId);
      setLead(l);
      onChanged?.(leadId);
    } catch { /* keep current state */ }
  };

  const nextTask = useMemo(() => {
    if (!lead?.tasks?.length) return null;
    // Pending/in_progress tasks come back ordered by due_at asc.
    // Prefer the first non-completed task as the "primary" follow-up.
    return lead.tasks.find((t) => t.status !== "completed" && t.status !== "cancelled") ?? lead.tasks[0];
  }, [lead]);

  const timing = followUpTiming(nextTask?.due_at, nextTask?.completed_at);

  /* Last customer interaction — most recent activity row whose meta
   * type suggests a touchpoint. Falls back to the first activity. */
  const lastInteraction = useMemo(() => {
    const acts = lead?.activities || [];
    return acts.find((a: any) => ["contact_logged", "email_received", "email_sent", "sms_sent", "sms_failed", "sms_received"].includes(a.type)) || acts[0] || null;
  }, [lead]);

  const recentActivities = useMemo(() => (lead?.activities || []).slice(0, 5), [lead]);

  const assigneeName = lead?.assignee
    ? [lead.assignee.first_name, lead.assignee.last_name].filter(Boolean).join(" ").trim() || lead.assignee.username
    : null;

  const openAi = async () => {
    setShowAi((s) => !s);
    if (aiSuggestions || aiLoading || !lead) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const rows = await fetchAiSuggestions(lead.id);
      setAiSuggestions(Array.isArray(rows) ? rows : []);
    } catch (e: any) {
      setAiError(e?.message || "AI suggestions unavailable");
    }
    setAiLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : "14px", overflow: "hidden" } }}
    >
      {/* ── Header bar — name, event, status, priority, assignee. The
           left vertical rail uses the follow-up timing color so the
           operational state reads at a glance. */}
      <Box sx={{
        position: "relative",
        px: 3, py: 2.4,
        borderBottom: `1px solid ${HAIRLINE}`,
        bgcolor: "#fff",
      }}>
        {/* Timing rail */}
        <Box sx={{
          position: "absolute", left: 0, top: 10, bottom: 10,
          width: 4, borderRadius: "0 3px 3px 0",
          bgcolor: timing.color,
        }} />

        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Box minWidth={0} flex={1}>
            {loading ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>Loading follow-up…</Typography>
              </Stack>
            ) : !lead ? (
              <Typography sx={{ fontSize: 13, color: "#dc2626" }}>{error ?? "Lead unavailable"}</Typography>
            ) : (
              <>
                <Stack direction="row" alignItems="center" gap={0.8} flexWrap="wrap" useFlexGap>
                  <Typography sx={{
                    fontSize: 18, fontWeight: 800, color: INK,
                    textTransform: "capitalize", lineHeight: 1.2,
                  }}>
                    {(lead.name || "Unknown").toLowerCase()}
                  </Typography>
                  {lead.lead_number && (
                    <Typography sx={{
                      fontFamily: FONT_MONO, fontSize: 10.5, fontWeight: 600,
                      color: "#94a3b8",
                    }}>
                      {lead.lead_number}
                    </Typography>
                  )}
                  {lead.is_vip && (
                    <Chip label="VIP" size="small" sx={{
                      height: 18, fontSize: 9, fontWeight: 800,
                      letterSpacing: 0.4, textTransform: "uppercase",
                      bgcolor: CHAMPAGNE_PALE, color: CHAMPAGNE_DEEP,
                    }} />
                  )}
                </Stack>

                <Stack direction="row" alignItems="center" gap={1} mt={0.6} flexWrap="wrap" useFlexGap>
                  {lead.event_type && (
                    <Box sx={{
                      px: 0.8, py: 0.2, borderRadius: "5px",
                      bgcolor: CHAMPAGNE_PALE, color: CHAMPAGNE_DEEP,
                      fontSize: 10.5, fontWeight: 700,
                      textTransform: "capitalize", letterSpacing: 0.2,
                    }}>
                      {String(lead.event_type).replace(/_/g, " ")}
                    </Box>
                  )}
                  {lead.event_date && (
                    <Stack direction="row" alignItems="center" gap={0.3}>
                      <CalendarMonthOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>{fmtDate(lead.event_date)}</Typography>
                    </Stack>
                  )}
                  {lead.guest_count != null && (
                    <Stack direction="row" alignItems="center" gap={0.3}>
                      <GroupOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>{lead.guest_count} guests</Typography>
                    </Stack>
                  )}
                </Stack>

                <Stack direction="row" alignItems="center" gap={0.8} mt={0.9} flexWrap="wrap" useFlexGap>
                  {/* Status */}
                  <Box sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.4,
                    px: 0.9, py: 0.25, borderRadius: "999px",
                    bgcolor: `${STATUS_COLOR[lead.status] ?? "#64748b"}14`,
                    color: STATUS_COLOR[lead.status] ?? "#64748b",
                    fontSize: 10, fontWeight: 700,
                    letterSpacing: "0.04em", textTransform: "uppercase",
                  }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: STATUS_COLOR[lead.status] ?? "#64748b" }} />
                    {STATUS_LABEL[lead.status] ?? lead.status}
                  </Box>
                  {/* Priority */}
                  <Box sx={{
                    px: 0.8, py: 0.2, borderRadius: "5px",
                    border: `1px solid ${PRIORITY_COLOR[lead.priority]}33`,
                    color: PRIORITY_COLOR[lead.priority],
                    fontSize: 10, fontWeight: 700,
                    letterSpacing: "0.04em", textTransform: "uppercase",
                  }}>
                    {lead.priority} priority
                  </Box>
                  {/* Assignee */}
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    {lead.assignee ? (
                      <>
                        <UserAvatar
                          user={{ profile_image: lead.assignee.profile_image, name: assigneeName || "?" }}
                          size={18}
                          tooltip={false}
                        />
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>
                          {assigneeName}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <PersonOffIcon sx={{ fontSize: 13, color: "#f97316" }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#f97316" }}>Unassigned</Typography>
                      </>
                    )}
                  </Stack>
                </Stack>
              </>
            )}
          </Box>

          <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
            <Tooltip title="Open full lead page">
              <IconButton
                size="small"
                onClick={() => { if (lead) navigate(`/leads/${lead.id}`); }}
                disabled={!lead}
                sx={{
                  color: "#64748b",
                  "&:hover": { color: INK, bgcolor: "#f1f5f9" },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose} sx={{ color: "#64748b" }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 0, bgcolor: "#fafbfc" }}>
        {loading && !lead ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !lead ? (
          <Box sx={{ p: 4 }}>
            <Alert severity="error" sx={{ fontSize: 12.5 }}>{error || "Lead could not be loaded."}</Alert>
          </Box>
        ) : (
          <Stack divider={<Divider />}>
            {/* ── FOLLOW-UP DETAILS ─────────────────────────────── */}
            <Box sx={{ p: 2.4, bgcolor: "#fff" }}>
              <Stack direction="row" alignItems="center" gap={1} mb={1.2}>
                <AccessTimeIcon sx={{ fontSize: 16, color: timing.color }} />
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: INK, letterSpacing: 0.3 }}>
                  Follow-up
                </Typography>
                <Chip
                  size="small"
                  label={timing.label}
                  sx={{
                    height: 18, fontSize: 9.5, fontWeight: 800,
                    letterSpacing: 0.4, textTransform: "uppercase",
                    bgcolor: `${timing.color}14`, color: timing.color,
                    border: `1px solid ${timing.color}33`,
                  }}
                />
              </Stack>

              {nextTask ? (
                <Stack gap={0.8}>
                  <DetailLine label="Title" value={nextTask.title} />
                  <DetailLine label="Due" value={fmtDateTime(nextTask.due_at)} valueColor={timing.color} />
                  <DetailLine label="Created" value={fmtDateTime(nextTask.created_at)} />
                  <DetailLine
                    label="Created by"
                    value={
                      nextTask.assignee
                        ? [nextTask.assignee.first_name, nextTask.assignee.last_name].filter(Boolean).join(" ").trim()
                          || nextTask.assignee.username
                        : "—"
                    }
                  />
                  {nextTask.notes && (
                    <Box sx={{
                      mt: 0.6, p: 1.2, borderRadius: "8px",
                      bgcolor: "#fafbfc", border: `1px solid ${HAIRLINE}`,
                    }}>
                      <Typography sx={{ fontSize: 12, color: "#475569", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                        {nextTask.notes}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                  No scheduled follow-up for this lead.
                </Typography>
              )}
            </Box>

            {/* ── LAST CUSTOMER INTERACTION ─────────────────────── */}
            {lastInteraction && (
              <Box sx={{ p: 2.4, bgcolor: "#fff" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: INK, letterSpacing: 0.3, mb: 1 }}>
                  Last Interaction
                </Typography>
                <ActivityRow activity={lastInteraction} />
              </Box>
            )}

            {/* ── RECENT ACTIVITY ──────────────────────────────── */}
            {recentActivities.length > 0 && (
              <Box sx={{ p: 2.4, bgcolor: "#fff" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: INK, letterSpacing: 0.3, mb: 1 }}>
                  Recent Activity
                </Typography>
                <Stack gap={0.3} divider={
                  <Box sx={{ height: "1px", bgcolor: HAIRLINE, mx: 1 }} />
                }>
                  {recentActivities.map((a: any) => (
                    <ActivityRow key={a.id} activity={a} density="compact" />
                  ))}
                </Stack>
              </Box>
            )}

            {/* ── AI ASSIST ─────────────────────────────────────── */}
            <Box sx={{ p: 2.4, bgcolor: "#fff" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Stack direction="row" alignItems="center" gap={0.6}>
                  <AutoAwesomeIcon sx={{ fontSize: 14, color: CHAMPAGNE_DEEP }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: INK, letterSpacing: 0.3 }}>
                    AI Assist
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={aiLoading ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <AutoAwesomeIcon sx={{ fontSize: 13 }} />}
                  onClick={openAi}
                  disabled={aiLoading}
                  sx={{
                    textTransform: "none", fontSize: 11, fontWeight: 700,
                    color: CHAMPAGNE_DEEP, borderColor: `${CHAMPAGNE}88`,
                    "&:hover": { borderColor: CHAMPAGNE_DEEP, bgcolor: CHAMPAGNE_PALE },
                  }}
                >
                  {showAi ? "Hide Suggestions" : "AI Suggestion"}
                </Button>
              </Stack>
              <Collapse in={showAi} unmountOnExit>
                <Box mt={1.2}>
                  {aiError ? (
                    <Alert severity="warning" sx={{ fontSize: 11.5 }}>{aiError}</Alert>
                  ) : aiSuggestions && aiSuggestions.length === 0 ? (
                    <Typography sx={{ fontSize: 11.5, color: "#94a3b8" }}>
                      No suggestions yet — the AI needs more context on this lead.
                    </Typography>
                  ) : aiSuggestions ? (
                    <Stack gap={1}>
                      {aiSuggestions.map((s, i) => (
                        <Box key={i} sx={{
                          p: 1.2, borderRadius: "8px",
                          bgcolor: CHAMPAGNE_PALE,
                          border: `1px solid ${CHAMPAGNE}33`,
                        }}>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: CHAMPAGNE_DEEP, letterSpacing: 0.2, textTransform: "uppercase", mb: 0.3 }}>
                            {s.title}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK, mb: 0.3 }}>
                            {s.value}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.4 }}>
                            {s.reason}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : null}
                </Box>
              </Collapse>
            </Box>

            {/* ── QUICK ACTIONS — reuse the exact same component used on
                  the lead detail page so the inline forms behave 1:1. */}
            <Box sx={{ p: 2.4, bgcolor: "#fff" }}>
              <QuickActionsCard
                lead={lead}
                teamUsers={teamUsers}
                onChanged={refresh}
                readOnly={lead.can_edit === false}
              />
            </Box>

          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── DetailLine — compact label/value row used in the follow-up panel. */
function DetailLine({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography sx={{
        fontSize: 10, fontWeight: 800, color: "#94a3b8",
        letterSpacing: 0.5, textTransform: "uppercase",
        minWidth: 86,
      }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: valueColor || INK }}>
        {value}
      </Typography>
    </Stack>
  );
}
